import {
    useInfiniteQuery,
    UseInfiniteQueryOptions,
    UseInfiniteQueryResult,
} from '@tanstack/react-query'
import { ReactNode } from 'react'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { AxiosError } from 'axios'
import { Spinner } from '@vnetwork/datacentral-ui/primitives'
import { AlertError } from '@/components/AlertError'

type ErrorDisplayMode = 'alert' | 'page'

/**
 * Extract HTTP status code from error object
 */
const getErrorStatusCode = (error: unknown): number => {
    // Check if it's an AxiosError with response
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError
        return axiosError.response?.status ?? 500
    }

    // Check if error has status property
    if (error && typeof error === 'object' && 'status' in error) {
        const statusError = error as { status: number }
        return statusError.status
    }

    // Default to 500 for unknown errors
    return 500
}

interface InfiniteQueryHandlerOptions<TError = Error> {
    /**
     * Custom loading component to render
     * @example loadingComponent: <Loading.CircularProgress width={40} height={40} />
     */
    loadingComponent?: ReactNode
    /**
     * Error display mode: 'alert' shows inline alert, 'page' shows full error page
     * @default 'alert'
     */
    errorDisplayMode?: ErrorDisplayMode
    /**
     * Custom error component to render when query has error (has highest priority)
     */
    renderError?: (error: TError) => ReactNode
    /**
     * Whether to show loading state (default: true)
     */
    showLoading?: boolean
    /**
     * Whether to show error state (default: true)
     */
    showError?: boolean
}

type UseInfiniteQueryWithHandlerResult<
    TData = unknown,
    TError = Error,
> = UseInfiniteQueryResult<TData, TError> & {
    /**
     * Returns the state component (loading/error) if applicable, otherwise null
     * Use this to render loading/error states in your component
     */
    renderState: () => ReactNode
}

/**
 * Custom hook wrapper around useInfiniteQuery that provides standardized error and loading handling
 *
 * @example
 * ```tsx
 * const { data, fetchNextPage, hasNextPage, renderState } = useInfiniteQueryWithHandler({
 *   queryFn: ({ pageParam = 1 }) => getJourneys({ page: pageParam }),
 *   queryKey: ['journeys'],
 *   getNextPageParam: (lastPage) => lastPage.nextPage,
 * })
 *
 * const stateComponent = renderState()
 * if (stateComponent) return stateComponent
 *
 * return <YourInfiniteScrollContent data={data} onLoadMore={fetchNextPage} />
 * ```
 *
 * @example With custom loading component
 * ```tsx
 * const { data, renderState } = useInfiniteQueryWithHandler(
 *   {
 *     queryFn: ({ pageParam = 1 }) => fetchData(pageParam),
 *     queryKey: ['data'],
 *     getNextPageParam: (lastPage) => lastPage.nextPage,
 *   },
 *   {
 *     loadingComponent: <Loading.CircularProgress width={40} height={40} />,
 *   }
 * )
 * ```
 *
 * @example With error page mode (full page error instead of inline alert)
 * ```tsx
 * const { data, renderState } = useInfiniteQueryWithHandler(
 *   {
 *     queryFn: ({ pageParam = 1 }) => fetchData(pageParam),
 *     queryKey: ['data'],
 *     getNextPageParam: (lastPage) => lastPage.nextPage,
 *   },
 *   {
 *     errorDisplayMode: 'page', // Shows ErrorPage component
 *   }
 * )
 * ```
 */
export const useInfiniteQueryWithHandler = <
    TData = unknown,
    TError = Error,
    TQueryFnData = unknown,
    TQueryKey extends readonly unknown[] = readonly unknown[],
    TPageParam = unknown,
>(
    options: UseInfiniteQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
    >,
    handlers?: InfiniteQueryHandlerOptions<TError>
): UseInfiniteQueryWithHandlerResult<TData, TError> => {
    const query = useInfiniteQuery<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
    >(options)

    const renderState = (): ReactNode => {
        // Handle loading state
        if (query.isLoading && handlers?.showLoading !== false) {
            return handlers?.loadingComponent ?? <Spinner />
        }

        // Handle error state
        if (query.isError && handlers?.showError !== false) {
            // Priority: renderError > errorDisplayMode > default (alert)
            if (handlers?.renderError) {
                return handlers.renderError(query.error as TError)
            }

            const errorDisplayMode = handlers?.errorDisplayMode ?? 'alert'
            if (errorDisplayMode === 'page') {
                const statusCode = getErrorStatusCode(query.error)
                return (
                    <ErrorPage
                        status={statusCode}
                        error={query.error as Error}
                    />
                )
            }

            return <AlertError error={query.error as Error} />
        }

        return null
    }

    return {
        ...query,
        renderState,
    }
}
